import { ReactElement, cloneElement, memo } from 'react';

import { Control, useFormContext, useFormState } from 'react-hook-form';

interface TextInputFormControllerProps {
  name: string;
  control: Control<any>;
  children: ReactElement;
}

/**
 * FormController specifically for text inputs using register instead of Controller.
 * This avoids re-renders on every keystroke since register doesn't trigger React re-renders
 * for every onChange event like Controller does.
 *
 * Performance benefit: Only re-renders when validation state changes, not on every keystroke.
 */
export const TextInputFormController = memo(
  ({ name, control, children }: TextInputFormControllerProps) => {
    const { register } = useFormContext();

    // Only subscribe to error state for this specific field
    const { errors } = useFormState({ control, name });

    // Get field registration props
    const { ref, ...fieldProps } = register(name);

    // Helper to get nested error by path
    const getFieldError = () => {
      const pathParts = name.split('.');
      let error: any = errors;

      for (const part of pathParts) {
        // Handle array indices like "insured_medical[0]"
        const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);
        if (arrayMatch) {
          const [, arrayName, index] = arrayMatch;
          error = error?.[arrayName]?.[parseInt(index, 10)];
        } else {
          error = error?.[part];
        }

        if (!error) return undefined;
      }

      return Array.isArray(error) ? error[0]?.message : error?.message || undefined;
    };

    return cloneElement(children, {
      ...fieldProps, // Spread register props (onChange, onBlur, name)
      ...children.props, // Preserve original props
      ref: ref,
      error: getFieldError(),
      key: name
    });
  }
);

TextInputFormController.displayName = 'TextInputFormController';
